// Owner-supplied Terms of Use content (July 2026), rendered ABOVE the pre-existing
// Terms on the same /terms page. Existing content is preserved untouched below.
const SECTIONS = [
  {
    n: 1,
    title: "About Nova",
    blocks: [
      { type: "p", text: "Nova is an AI-powered personal finance platform designed to help users:" },
      { type: "ul", items: [
        "Track debts and financial goals", "Monitor expenses", "Analyse bank statements",
        "Create repayment plans", "Receive AI-powered financial insights", "Organise recurring bills",
        "Store financial information securely", "Generate reports and financial summaries",
      ] },
      { type: "p", text: "Nova is intended to help users better understand their finances. It is not a bank, lender, financial institution, investment platform, accountant, solicitor, or regulated financial adviser." },
    ],
  },
  {
    n: 2,
    title: "Eligibility",
    blocks: [
      { type: "p", text: "To use Nova you must:" },
      { type: "ul", items: [
        "Be at least 18 years old, or the legal age in your country.",
        "Have the legal authority to agree to these Terms.",
        "Use the Services only for lawful purposes.",
      ] },
    ],
  },
  {
    n: 3,
    title: "User Accounts",
    blocks: [
      { type: "p", text: "When creating an account you agree to:" },
      { type: "ul", items: [
        "Provide accurate information.", "Keep your information up to date.",
        "Protect your password.", "Keep your login credentials confidential.",
        "Notify us immediately if you believe your account has been compromised.",
      ] },
      { type: "p", text: "You are responsible for all activity that occurs under your account." },
    ],
  },
  {
    n: 4,
    title: "AI-Powered Features",
    blocks: [
      { type: "p", text: "Nova uses artificial intelligence to provide:" },
      { type: "ul", items: [
        "Financial insights", "Spending analysis", "Debt repayment suggestions",
        "Bank statement analysis", "Financial coaching", "Expense categorisation",
        "Forecasts and predictions",
      ] },
      { type: "p", text: "Although we aim for accuracy, AI-generated content:" },
      { type: "ul", items: [
        "May be incomplete", "May contain errors", "May not reflect your full financial situation",
        "Should not be relied upon as professional financial advice",
      ] },
      { type: "p", text: "You remain solely responsible for any financial decisions you make." },
    ],
  },
  {
    n: 5,
    title: "Bank Statement Analysis",
    blocks: [
      { type: "p", text: "If you upload bank statements:" },
      { type: "ul", items: [
        "You confirm you have permission to upload them.",
        "Files are analysed solely to provide Nova’s features.",
        "Analysis results depend on the quality and completeness of the uploaded data.",
        "Nova may automatically identify recurring expenses, subscriptions, income sources, and spending categories.",
      ] },
      { type: "p", text: "You should review all AI-generated results before acting upon them." },
    ],
  },
  {
    n: 6,
    title: "Financial Information",
    blocks: [
      { type: "p", text: "Nova may process information you choose to provide, including:" },
      { type: "ul", items: [
        "Debts", "Savings goals", "Expenses", "Income", "Budgets",
        "Repayment plans", "Uploaded financial documents",
      ] },
      { type: "p", text: "You are responsible for ensuring the information entered into Nova is accurate." },
    ],
  },
  {
    n: 7,
    title: "Cloud Backup",
    blocks: [
      { type: "p", text: "If Cloud Backup is enabled:" },
      { type: "ul", items: [
        "Your Nova data may be securely backed up.",
        "You may restore backups to your account.",
        "You may delete your backups at any time.",
        "We recommend keeping your account credentials secure.",
      ] },
      { type: "p", text: "Although we use reasonable safeguards, no online storage system can be guaranteed to be completely secure." },
    ],
  },
  {
    n: 8,
    title: "Accounts and Authentication",
    blocks: [
      { type: "p", text: "Nova may support:" },
      { type: "ul", items: ["Email and password", "Sign in with Apple", "Sign in with Google"] },
      { type: "p", text: "Third-party authentication providers operate under their own terms and privacy policies." },
    ],
  },
  {
    n: 9,
    title: "Acceptable Use",
    blocks: [
      { type: "p", text: "You agree not to:" },
      { type: "ul", items: [
        "Attempt to reverse engineer Nova.", "Interfere with our systems.",
        "Upload malicious software.", "Use Nova for unlawful purposes.",
        "Attempt unauthorised access.", "Abuse AI services.",
        "Circumvent security measures.", "Use automated tools to overload the platform.",
      ] },
    ],
  },
  {
    n: 10,
    title: "Availability",
    blocks: [
      { type: "p", text: "We aim to keep Nova available at all times." },
      { type: "p", text: "However, services may occasionally be unavailable due to:" },
      { type: "ul", items: [
        "Maintenance", "Security updates", "Third-party outages", "Internet failures", "Cloud infrastructure issues",
      ] },
      { type: "p", text: "We cannot guarantee uninterrupted availability." },
    ],
  },
  {
    n: 11,
    title: "Intellectual Property",
    blocks: [
      { type: "p", text: "Nova, including its:" },
      { type: "ul", items: [
        "Software", "Branding", "Logos", "Graphics", "AI systems", "Interface", "Designs", "Documentation",
      ] },
      { type: "p", text: "remain the intellectual property of Nova and its licensors." },
      { type: "p", text: "You may not copy, redistribute, modify, or resell any part of Nova without written permission." },
    ],
  },
  {
    n: 12,
    title: "User Content",
    blocks: [
      { type: "p", text: "You retain ownership of the information you upload." },
      { type: "p", text: "By using Nova you grant us the limited rights necessary to:" },
      { type: "ul", items: [
        "Store your information", "Process your information", "Analyse uploaded files",
        "Generate AI responses", "Operate the Services",
      ] },
      { type: "p", text: "We do not claim ownership of your financial data." },
    ],
  },
  {
    n: 13,
    title: "Subscriptions",
    blocks: [
      { type: "p", text: "Some Nova features may require a paid subscription." },
      { type: "p", text: "Where subscriptions are offered:" },
      { type: "ul", items: [
        "Prices will be displayed before purchase.",
        "Payments are processed through the Apple App Store on iOS.",
        "Subscriptions automatically renew unless cancelled.",
        "Payment is charged to the user’s Apple Account.",
        "Renewal and cancellation are managed through Apple subscription settings.",
        "Cancellation normally takes effect at the end of the current paid subscription period.",
        "Refunds are governed by Apple’s applicable policies.",
        "Trial or introductory offers apply only where displayed and confirmed by Apple.",
        "Local prices may vary according to the user’s App Store country or region.",
      ] },
      { type: "p", text: "Nova currently offers the following intended UK launch prices:" },
      { type: "ul", items: ["£1.99 per week", "£3.99 per month", "£29.99 per year"] },
      { type: "p", text: "Apple may display different local prices depending on the user’s App Store region." },
    ],
  },
  {
    n: 14,
    title: "Beta Features",
    blocks: [
      { type: "p", text: "Some features may be marked as:" },
      { type: "ul", items: ["Beta", "Preview", "Experimental"] },
      { type: "p", text: "These features may change, be modified, or be removed without notice." },
    ],
  },
  {
    n: 15,
    title: "Limitation of Liability",
    blocks: [
      { type: "p", text: "To the fullest extent permitted by law, Nova and its developers shall not be liable for:" },
      { type: "ul", items: [
        "Financial losses", "Missed payments", "Investment decisions", "Credit score impacts",
        "Lost profits", "Indirect damages", "Consequential damages",
        "Data loss caused by circumstances beyond our reasonable control",
      ] },
      { type: "p", text: "Your use of Nova is entirely at your own risk." },
      { type: "p", text: "Nothing in these Terms excludes or limits liability where doing so would be unlawful, including liability that cannot legally be excluded under applicable consumer law." },
    ],
  },
  {
    n: 16,
    title: "No Financial Advice",
    blocks: [
      { type: "p", text: "Nova provides educational tools and organisational features only." },
      { type: "p", text: "Nothing within Nova should be interpreted as:" },
      { type: "ul", items: ["Financial advice", "Investment advice", "Tax advice", "Accounting advice", "Legal advice"] },
      { type: "p", text: "Where appropriate, you should seek advice from a qualified professional." },
    ],
  },
  {
    n: 17,
    title: "Account Termination",
    blocks: [
      { type: "p", text: "You may delete your account at any time from within the application." },
      { type: "p", text: "We may suspend or terminate accounts that:" },
      { type: "ul", items: [
        "Violate these Terms", "Abuse our systems", "Attempt fraudulent activity", "Threaten platform security",
      ] },
      { type: "p", text: "Where required by applicable law, users will retain any mandatory rights relating to their data, purchases or subscriptions." },
    ],
  },
  {
    n: 18,
    title: "Changes to These Terms",
    blocks: [
      { type: "p", text: "We may update these Terms from time to time." },
      { type: "p", text: "Where changes are significant, we will notify users within the app or by other appropriate means. Continued use of Nova after updated Terms take effect constitutes acceptance of the revised Terms." },
    ],
  },
  {
    n: 19,
    title: "Governing Law",
    blocks: [
      { type: "p", text: "These Terms are governed by the laws of England and Wales, unless mandatory local consumer laws require otherwise." },
      { type: "p", text: "Nothing in these Terms removes any mandatory consumer rights available under applicable law." },
    ],
  },
  {
    n: 20,
    title: "Contact Us",
    blocks: [
      { type: "p", text: "If you have any questions about these Terms, you can contact us through the in-app Support Centre or via the support contact published on our official website." },
    ],
  },
];

const Block = ({ block }) =>
  block.type === "ul" ? (
    <ul className="mt-2 grid gap-2 sm:grid-cols-2">
      {block.items.map((it, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-nova-emerald" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  ) : (
    <p>{block.text}</p>
  );

export const NewTermsOfUse = () => (
  <div data-testid="terms-new-content" className="space-y-10">
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-white/40">Effective July 2026 · Last updated July 2026</p>
      <div className="mt-4 space-y-3 text-[15px] leading-relaxed text-white/70">
        <p>Welcome to Nova.</p>
        <p>
          These Terms of Use (“Terms”) govern your access to and use of the Nova application, website, and
          related services (collectively, the “Services”). By creating an account, accessing, or using Nova,
          you agree to these Terms. If you do not agree, please do not use Nova.
        </p>
      </div>
    </div>

    {SECTIONS.map((s) => (
      <section key={s.n} className="scroll-mt-28">
        <h3 className="font-heading text-lg font-bold text-white sm:text-xl">{`${s.n}. ${s.title}`}</h3>
        <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-white/65">
          {s.blocks.map((b, i) => (
            <Block key={i} block={b} />
          ))}
        </div>
      </section>
    ))}

    <div className="space-y-3 text-[15px] leading-relaxed text-white/65">
      <p className="text-xs uppercase tracking-[0.2em] text-white/40">Last updated July 2026</p>
      <p>By creating an account or continuing to use Nova, you confirm that you have read, understood, and agree to these Terms of Use.</p>
    </div>
  </div>
);

export default NewTermsOfUse;
